// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useGetAllInstrumentConfigurations } from '@hooks/queries/useGetAllInstrumentConfigurations';
import React from 'react';
import { Close } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  IconButton,
  LinearProgress,
  MenuItem,
  MenuList,
  Popover,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import { grey } from '@mui/material/colors';

interface InstrumentSelectionsProps {
  selectedInstruments?: { instrumentId: string; registrar: string }[];
  multiple?: boolean;
  onInstrumentsChange?: (instruments: { instrumentId: string; registrar: string }[]) => void;
  allInstruments?: { instrumentId: string; registrar: string; contractId?: string }[];
  hideRegistrar?: boolean;
}
interface InstProps {
  instrumentId: string;
  registrar: string;
  hideRegistrar?: boolean;
}
const InstrumentLayout: React.FC<InstProps> = ({ instrumentId, registrar, hideRegistrar }) => {
  return (
    <Box sx={{ width: '100%' }}>
      <Typography
        sx={{
          width: '100%',
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        variant="body2"
        fontWeight="bold"
      >
        {instrumentId}
      </Typography>
      {!hideRegistrar && (
        <Typography
          sx={{
            width: '100%',
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          variant="caption"
        >
          {registrar}
        </Typography>
      )}
    </Box>
  );
};

export const InstrumentSelections: React.FC<InstrumentSelectionsProps> = ({
  selectedInstruments = [],
  multiple = false,
  onInstrumentsChange,
  allInstruments,
  hideRegistrar = false,
}) => {
  const instconfigs = useGetAllInstrumentConfigurations();
  const simpleInstConfigs =
    allInstruments !== undefined
      ? allInstruments
      : instconfigs.data?.instrumentConfigurations.map((i) => ({
          contractId: i.contract.contractId,
          registrar: i.payload.defaultIdentifier.source,
          instrumentId: i.payload.defaultIdentifier.id,
        })) || [];
  const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null);
  // No selectedIndex needed for unified multi/single select
  const [internalSelectedInstruments, setInternalSelectedInstruments] = React.useState<
    { instrumentId: string; registrar: string }[]
  >(selectedInstruments || []);
  const { isLoading } = instconfigs;
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const [input, setInput] = React.useState('');
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
  };
  const open = Boolean(anchorEl);
  const handleClose = () => {
    setAnchorEl(null);
  };
  const id = open ? 'instrument-select' : undefined;
  const handleMenuItemClick = (
    _: React.MouseEvent<HTMLElement>,
    _index: number,
    selectedInstrumentId: string,
    selectedRegistrar: string,
  ) => {
    if (multiple) {
      // Toggle selection
      const exists = internalSelectedInstruments.some(
        (i) => i.instrumentId === selectedInstrumentId && i.registrar === selectedRegistrar,
      );
      let newSelected;
      if (exists) {
        newSelected = internalSelectedInstruments.filter(
          (i) => !(i.instrumentId === selectedInstrumentId && i.registrar === selectedRegistrar),
        );
      } else {
        newSelected = [
          ...internalSelectedInstruments,
          { instrumentId: selectedInstrumentId, registrar: selectedRegistrar },
        ];
      }
      setInternalSelectedInstruments(newSelected);
      onInstrumentsChange?.(newSelected);
      // Do not close popover on multi-select
    } else {
      // Single select: only one instrument in array
      const newSelected = [{ instrumentId: selectedInstrumentId, registrar: selectedRegistrar }];
      setInternalSelectedInstruments(newSelected);
      onInstrumentsChange?.(newSelected);
      handleClose();
    }
  };

  const filteredConfigs = simpleInstConfigs.filter((i) =>
    i.instrumentId.toLowerCase().includes(input.toLowerCase()),
  );

  const hasSelectedInstrument = internalSelectedInstruments.length > 0;
  return (
    <>
      <Box
        sx={(t) => ({
          cursor: onInstrumentsChange ? 'pointer' : 'default',
          borderRadius: 1,
          border: `1px solid ${t.palette.divider}`,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          p: 1,
        })}
        onClick={onInstrumentsChange ? handleClick : undefined}
      >
        <Box sx={{ width: '100%', overflow: 'hidden' }}>
          {hasSelectedInstrument ? (
            internalSelectedInstruments.map((inst) => (
              <InstrumentLayout
                key={`${inst.instrumentId}:${inst.registrar}`}
                instrumentId={inst.instrumentId}
                registrar={inst.registrar}
                hideRegistrar={hideRegistrar}
              />
            ))
          ) : (
            <Typography sx={{ color: grey[500] }}>
              {multiple ? 'Select Instruments' : 'Select Instrument'}
            </Typography>
          )}
        </Box>
        {onInstrumentsChange && (
          <IconButton sx={{ ml: 'auto' }} size="small">
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        sx={{ maxWidth: '600px' }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Box sx={{ display: 'relative', p: 1 }}>
          <Toolbar sx={{ alignItems: 'center' }} disableGutters variant="dense">
            <Typography fontWeight="bold">Select Instrument{multiple ? 's' : ''}</Typography>
            <IconButton onClick={handleClose} sx={{ ml: 'auto' }}>
              <Close fontSize="small" color="action" />
            </IconButton>
          </Toolbar>
          <TextField
            onChange={onChange}
            value={input}
            sx={{ position: 'sticky', top: 0 }}
            size="small"
            autoComplete="off"
            placeholder="search by Instrument ID"
            fullWidth
          />
          {isLoading && <LinearProgress />}
          <Box sx={{ mt: 1, overflowY: 'auto', minWidth: 400 }}>
            <MenuList sx={{ mt: 0, maxHeight: 300 }}>
              {filteredConfigs?.map((instConfig, index) => {
                const checked = internalSelectedInstruments.some(
                  (i) =>
                    i.instrumentId === instConfig.instrumentId &&
                    i.registrar === instConfig.registrar,
                );
                return (
                  <MenuItem
                    onClick={(e) =>
                      handleMenuItemClick(e, index, instConfig.instrumentId, instConfig.registrar)
                    }
                    divider
                    disableGutters
                    selected={checked}
                    key={instConfig.contractId}
                  >
                    {multiple && (
                      <input
                        type="checkbox"
                        checked={checked}
                        readOnly
                        style={{ marginRight: 8 }}
                      />
                    )}
                    <Box sx={{ p: 1, whiteSpace: 'nowrap', overflow: 'hidden' }}>
                      <InstrumentLayout
                        instrumentId={instConfig.instrumentId}
                        registrar={instConfig.registrar}
                        hideRegistrar={hideRegistrar}
                      />
                    </Box>
                  </MenuItem>
                );
              })}
            </MenuList>
            {filteredConfigs.length === 0 && (
              <Typography variant="body2" color="error">
                No instrument with ID {input} could be found
              </Typography>
            )}
          </Box>
        </Box>
      </Popover>
    </>
  );
};
